/**
 * /book wizard draft persistence.
 *
 * A prospect can explore availability + price without signing in. When they
 * reach the step that needs an account, we send a magic link whose callback
 * brings them back to `/book?draft=<id>…`. Everything the wizard collected
 * before that round trip lives in `localStorage` under a key scoped by tenant
 * host + draft id, so the wizard can pick up exactly where it left off.
 *
 * Storage is best-effort: every access is wrapped in try/catch (private
 * windows, disabled storage, quota) and callers must treat a `null` load as
 * "start fresh". Drafts expire after `DRAFT_TTL_MS` and carry a schema
 * version so a future shape change can't crash the wizard on stale data.
 */

export type GuestSpecies = 'dog' | 'cat';
export type GuestSizeCategory = 'small' | 'medium' | 'large' | 'extra_large';

/** A pet described inline by a visitor who has no pets on file (yet). */
export interface GuestPet {
  /** Stable client-side key used as the wizard-local pet id. */
  key: string;
  name: string;
  species: GuestSpecies;
  /** Empty string = not specified. */
  sizeCategory: GuestSizeCategory | '';
  breed: string;
}

export interface BookDraft {
  v: 1;
  savedAt: number;
  /** `URLSearchParams` string of the wizard URL (without `draft`). */
  params: string;
  guestPets: GuestPet[];
  runIdByPet: Record<string, string | null>;
  intakeAnswers: Record<string, Record<string, { answer: 0 | 1; note?: string | undefined }>>;
  discountCode: string;
  /**
   * Migration 0101 — structured feeding plan drafts keyed by wizard pet id
   * (real pet id, or the guest key before sign-in). Optional so drafts
   * written before this field existed still load.
   */
  feedingPlanByPet?: Record<string, unknown>;
}

export type BookDraftInput = Omit<BookDraft, 'v' | 'savedAt'>;

export const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const KEY_PREFIX = 'kennel:book-draft';
const ID_RE = /^[A-Za-z0-9_-]{8,64}$/;

export function newDraftId(): string {
  try {
    const c = globalThis.crypto;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID().replace(/-/g, '');
  } catch {
    // fall through
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

export function isValidDraftId(id: string | null | undefined): id is string {
  return typeof id === 'string' && ID_RE.test(id);
}

export function draftStorageKey(tenantKey: string, id: string): string {
  return `${KEY_PREFIX}:${tenantKey || 'default'}:${id}`;
}

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function saveBookDraft(tenantKey: string, id: string, draft: BookDraftInput): boolean {
  if (!isValidDraftId(id)) return false;
  const store = storage();
  if (!store) return false;
  const full: BookDraft = { v: 1, savedAt: Date.now(), ...draft };
  try {
    store.setItem(draftStorageKey(tenantKey, id), JSON.stringify(full));
    return true;
  } catch {
    return false;
  }
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isGuestPet(v: unknown): v is GuestPet {
  if (!isRecord(v)) return false;
  return (
    typeof v.key === 'string' &&
    typeof v.name === 'string' &&
    (v.species === 'dog' || v.species === 'cat') &&
    typeof v.sizeCategory === 'string' &&
    typeof v.breed === 'string'
  );
}

export function loadBookDraft(tenantKey: string, id: string, now = Date.now()): BookDraft | null {
  if (!isValidDraftId(id)) return null;
  const store = storage();
  if (!store) return null;
  const key = draftStorageKey(tenantKey, id);
  try {
    const raw = store.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed) || parsed.v !== 1) {
      store.removeItem(key);
      return null;
    }
    if (typeof parsed.savedAt !== 'number' || now - parsed.savedAt > DRAFT_TTL_MS) {
      store.removeItem(key);
      return null;
    }
    const guestPets = Array.isArray(parsed.guestPets) ? parsed.guestPets.filter(isGuestPet) : [];
    return {
      v: 1,
      savedAt: parsed.savedAt,
      params: typeof parsed.params === 'string' ? parsed.params : '',
      guestPets,
      runIdByPet: isRecord(parsed.runIdByPet)
        ? (parsed.runIdByPet as Record<string, string | null>)
        : {},
      intakeAnswers: isRecord(parsed.intakeAnswers)
        ? (parsed.intakeAnswers as BookDraft['intakeAnswers'])
        : {},
      discountCode: typeof parsed.discountCode === 'string' ? parsed.discountCode : '',
      feedingPlanByPet: isRecord(parsed.feedingPlanByPet)
        ? (parsed.feedingPlanByPet as Record<string, unknown>)
        : {},
    };
  } catch {
    return null;
  }
}

export function clearBookDraft(tenantKey: string, id: string): void {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(draftStorageKey(tenantKey, id));
  } catch {
    // ignore
  }
}

/**
 * Query-string form of guest pet descriptors for `GET /api/customer/availability`:
 * `dog:medium,cat`.
 */
export function guestPetsToParam(
  pets: ReadonlyArray<{ species: string; sizeCategory: string | null }>,
): string {
  return pets.map((p) => (p.sizeCategory ? `${p.species}:${p.sizeCategory}` : p.species)).join(',');
}

/** JSON-body form for `POST /api/customer/preview-quote`. */
export function guestPetsToDescriptors(pets: ReadonlyArray<GuestPet>): Array<{
  name: string | null;
  species: GuestSpecies;
  sizeCategory: GuestSizeCategory | null;
  breed: string | null;
}> {
  return pets.map((p) => ({
    name: p.name.trim() || null,
    species: p.species,
    sizeCategory: p.sizeCategory || null,
    breed: p.breed.trim() || null,
  }));
}
